import { t } from 'i18next';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import { Card } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { formatUtils } from '@/lib/utils';
import { AdminAccountsOverview } from '@activepieces/shared';

import { closedMonths } from './closed-months';

// The branding primary, set at runtime by the theme provider, so the charts
// follow the platform colour. Validated against both gray steps (dataviz
// palette checks, light and dark cards): the blue is the series that matters,
// the gray is context.
const EMPHASIS_COLOR: ColorConfig = { color: 'hsl(var(--primary))' };
const CONTEXT_COLOR: ColorConfig = {
  theme: { light: '#6b7280', dark: '#71717a' },
};
// Second categorical slot, validated with the two above on all pairs, since
// lines can cross.
const SECONDARY_COLOR: ColorConfig = {
  theme: { light: '#eb6834', dark: '#d95926' },
};

const LINE_WIDTH = 2;
const DOT_RADIUS = 4;
const ACTIVE_DOT_RADIUS = 6;
const CHART_HEIGHT_CLASS = 'h-[200px] w-full';
const Y_AXIS_WIDTH = 40;
// Keeps the first and last dots off the card edges.
const X_AXIS_PADDING = { left: 16, right: 16 };

const compactNumber = new Intl.NumberFormat(undefined, {
  notation: 'compact',
});

// Periods are first-of-month dates: format in UTC so a negative offset does not
// shift them into the previous month.
const monthLabel = (period: string): string =>
  new Date(period).toLocaleDateString(undefined, {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });

type SeriesPoint = AdminAccountsOverview['series'][number];

type ColorConfig =
  | { color: string }
  | { theme: { light: string; dark: string } };

type LineSpec = {
  key: string;
  label: string;
  colorConfig: ColorConfig;
  read: (point: SeriesPoint) => number;
};

const MonthlyLineChart = ({
  title,
  series,
  lines,
}: {
  title: string;
  series: SeriesPoint[];
  lines: LineSpec[];
}) => {
  const config: ChartConfig = Object.fromEntries(
    lines.map((line) => [line.key, { label: line.label, ...line.colorConfig }]),
  );
  const data = series.map((point) => ({
    month: monthLabel(point.period),
    ...Object.fromEntries(lines.map((line) => [line.key, line.read(point)])),
  }));
  // A single series is named by the card title; several need a legend.
  const hasLegend = lines.length > 1;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="text-sm font-medium">{title}</h2>
      <ChartContainer config={config} className={CHART_HEIGHT_CLASS}>
        <LineChart accessibilityLayer data={data}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            padding={X_AXIS_PADDING}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: number) => compactNumber.format(value)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <span className="flex w-full justify-between gap-3">
                    <span className="text-muted-foreground">
                      {config[String(name)]?.label}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatUtils.formatNumber(Number(value))}
                    </span>
                  </span>
                )}
              />
            }
          />
          {hasLegend && <ChartLegend content={<ChartLegendContent />} />}
          {lines.map((line) => (
            <Line
              key={line.key}
              dataKey={line.key}
              type="linear"
              stroke={`var(--color-${line.key})`}
              strokeWidth={LINE_WIDTH}
              dot={{ r: DOT_RADIUS, fill: `var(--color-${line.key})` }}
              activeDot={{ r: ACTIVE_DOT_RADIUS }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </Card>
  );
};

// Closed months only: the current one is month-to-date and already sits in the
// tiles above. Every chart then shares the same months on its axis.
export const MonthlyCharts = ({ series }: { series: SeriesPoint[] }) => {
  const months = closedMonths(series, new Date());

  if (months.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">
          {t('Monthly history starts once a first month is complete.')}
        </p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <MonthlyLineChart
        title={t('Accounts')}
        series={months}
        lines={[
          {
            key: 'provisioned',
            label: t('Provisioned'),
            colorConfig: CONTEXT_COLOR,
            read: (point) => point.provisionedAccounts,
          },
          {
            key: 'active',
            label: t('Active (30d)'),
            colorConfig: EMPHASIS_COLOR,
            read: (point) => point.activeAccounts,
          },
          {
            key: 'mcp',
            label: t('MCP active (30d)'),
            colorConfig: SECONDARY_COLOR,
            read: (point) => point.mcpAccounts,
          },
        ]}
      />
      <MonthlyLineChart
        title={t('Enabled flows')}
        series={months}
        lines={[
          {
            key: 'enabledFlows',
            label: t('Enabled flows'),
            colorConfig: EMPHASIS_COLOR,
            read: (point) => point.totalEnabledFlows,
          },
        ]}
      />
      <MonthlyLineChart
        title={t('Tasks per month')}
        series={months}
        lines={[
          {
            key: 'tasks',
            label: t('Tasks'),
            colorConfig: EMPHASIS_COLOR,
            read: (point) => point.totalTasks,
          },
        ]}
      />
      <MonthlyLineChart
        title={t('MCP executions per month')}
        series={months}
        lines={[
          {
            key: 'mcpExecutions',
            label: t('MCP executions'),
            colorConfig: EMPHASIS_COLOR,
            read: (point) => point.totalMcpExecutions,
          },
        ]}
      />
    </div>
  );
};
